﻿interface Stat {
    percentage: string
    description: string
    source?: string
}

class StatsContainer extends HTMLElement {
    private stats: Stat[] = []
    private percentageEl: HTMLElement
    private descriptionEl: HTMLElement
    private sourceEl?: HTMLElement
    private pagerEl?: HTMLElement
    private currentIndex = 0
    private interval: number
    private animation: number
    private timer?: number

    constructor() {
        super()

        // Parse JSON from data-stats
        const rawStats = this.dataset.stats
        if (rawStats) {
            try {
                this.stats = JSON.parse(rawStats)
            } catch (e) {
                console.error("Invalid JSON in data-stats", e)
            }
        }

        this.interval = parseInt(this.dataset.interval || "3000", 10)
        this.animation = parseInt(this.dataset.animation || "300", 10)

        const isStacked = this.dataset.layout === "stacked"

        // Container styling
        this.style.display = "flex"
        this.style.alignItems = isStacked ? "stretch" : "center"
        this.style.justifyContent = isStacked ? "space-between" : "center"
        this.style.flexDirection = isStacked ? "column" : "row"
        this.style.gap = isStacked ? "0" : "2rem"

        // Percentage element
        this.percentageEl = document.createElement("div")
        if (this.dataset.percentageClass) {
            this.percentageEl.className = this.dataset.percentageClass
        }
        this.percentageEl.style.transition = `all ${this.animation}ms ease`

        // Description element
        this.descriptionEl = document.createElement("div")
        if (this.dataset.descriptionClass) {
            this.descriptionEl.className = this.dataset.descriptionClass
        }
        this.descriptionEl.style.transition = `all ${this.animation}ms ease`

        this.appendChild(this.percentageEl)
        this.appendChild(this.descriptionEl)

        if (this.dataset.sourceClass) {
            this.sourceEl = document.createElement("div")
            this.sourceEl.className = this.dataset.sourceClass
            this.sourceEl.style.transition = `all ${this.animation}ms ease`
            this.appendChild(this.sourceEl)
        }

        if (this.dataset.showPager === "true") {
            this.pagerEl = document.createElement("div")
            this.pagerEl.className = "mt-auto flex items-center justify-between pt-8 text-xs text-white/80"
            this.appendChild(this.pagerEl)
        }

        if (this.stats.length > 0) {
            this.updateContent()
        }
    }

    connectedCallback() {
        if (this.stats.length > 1) {
            this.timer = window.setInterval(() => {
                this.animateOut()
                setTimeout(() => {
                    this.currentIndex = (this.currentIndex + 1) % this.stats.length
                    this.updateContent()
                    this.animateIn()
                }, this.animation)
            }, this.interval)
        }
    }

    disconnectedCallback() {
        if (this.timer) clearInterval(this.timer)
    }

    private updateContent() {
        const stat = this.stats[this.currentIndex]
        this.percentageEl.textContent = stat.percentage
        this.descriptionEl.textContent = stat.description
        if (this.sourceEl) this.sourceEl.textContent = stat.source || ""
        if (this.pagerEl) {
            const dots = this.stats.map((_, index) =>
                `<span class="${index === this.currentIndex ? "h-1.5 w-4 bg-white" : "h-1.5 w-1.5 bg-white/40"} rounded-full"></span>`
            ).join("")
            this.pagerEl.innerHTML = `<span class="flex items-center gap-3">${dots}</span><span>${this.currentIndex + 1} / ${this.stats.length}</span>`
        }
    }

    private animateOut() {
        this.percentageEl.style.opacity = "0"
        this.percentageEl.style.transform = "scale(0.95)"
        this.descriptionEl.style.opacity = "0"
        this.descriptionEl.style.transform = "translateX(1rem)"
        if (this.sourceEl) {
            this.sourceEl.style.opacity = "0"
            this.sourceEl.style.transform = "translateX(1rem)"
        }
    }

    private animateIn() {
        this.percentageEl.style.opacity = "1"
        this.percentageEl.style.transform = "scale(1)"
        this.descriptionEl.style.opacity = "1"
        this.descriptionEl.style.transform = "translateX(0)"
        if (this.sourceEl) {
            this.sourceEl.style.opacity = "1"
            this.sourceEl.style.transform = "translateX(0)"
        }
    }
}

customElements.define("stats-container", StatsContainer)
